import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { CheckCircle2, Save, History, Search, Users, AlertTriangle, CalendarDays, ChevronRight, Clock3, UserCheck, UserX, Sparkles } from "lucide-react";
import styles from "../../styles/Attendance.module.css";
import toast, { Toaster } from 'react-hot-toast';

export default function Attendance() {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [expandedLog, setExpandedLog] = useState(null);
  const [staffStatus, setStaffStatus] = useState([]);
  const [attendanceHistory, setAttendanceHistory] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [rosterRes, historyRes] = await Promise.all([
        axios.get('/api/attendance/roster'),
        axios.get('/api/attendance/history')
      ]);
      setStaffStatus(rosterRes.data.map(s => ({
        ...s,
        status: s.status || 'present',
        lastSeen: s.lastSeen || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      })));
      setAttendanceHistory(historyRes.data.map(h => ({
        ...h,
        details: Array.isArray(h.details) ? h.details : String(h.details || '').split('|').filter(Boolean).map(d => {
          const [name, status] = d.split(':');
          return { name, status };
        })
      })));
    } catch (err) {
      console.error("Attendance Sync Error:", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusChange = (id, status) => setStaffStatus(prev => prev.map(s => s.id === id ? { ...s, status } : s));
  const markAllPresent = () => setStaffStatus(prev => prev.map(s => ({ ...s, status: 'present' })));
  const submitAttendance = async () => {
    try {
      await axios.post('/api/attendance/submit', { date: attendanceDate, records: staffStatus.map(s => ({ staff_id: s.id, status: s.status })) });
      toast.success("Attendance saved successfully");
      fetchData();
    } catch (err) { toast.error("Unable to save attendance"); }
  };

  const counts = useMemo(() => ({
    present: staffStatus.filter(s => s.status === 'present').length,
    late: staffStatus.filter(s => s.status === 'late').length,
    absent: staffStatus.filter(s => s.status === 'absent').length
  }), [staffStatus]);
  const filtered = staffStatus.filter(s => `${s.name} ${s.role || ''}`.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) return <div className={styles.loading}>Preparing today’s attendance…</div>;

  return (
    <div className={styles.page}>
      <Toaster position="top-right" />
      <section className={styles.hero}>
        <div><span className={styles.eyebrow}>TEAM • ATTENDANCE</span><h1>Attendance Management</h1><p>Keep a clear daily record of who is available, late or away from the salon.</p></div>
        <div className={styles.heroDate}><CalendarDays size={20}/><div><small>Attendance date</small><input type="date" value={attendanceDate} onChange={e => setAttendanceDate(e.target.value)} /></div></div>
      </section>

      <section className={styles.stats}>
        <div className={styles.stat}><span className={styles.goldIcon}><Users size={21}/></span><div><small>Total Team</small><strong>{staffStatus.length}</strong><p>Staff on roster</p></div></div>
        <div className={styles.stat}><span className={styles.presentIcon}><UserCheck size={21}/></span><div><small>Present</small><strong>{counts.present}</strong><p>Available today</p></div></div>
        <div className={styles.stat}><span className={styles.lateIcon}><Clock3 size={21}/></span><div><small>Late</small><strong>{counts.late}</strong><p>Arrived late</p></div></div>
        <div className={styles.stat}><span className={styles.absentIcon}><UserX size={21}/></span><div><small>Absent</small><strong>{counts.absent}</strong><p>Not available</p></div></div>
      </section>

      <div className={styles.toolbar}>
        <div className={styles.search}><Search size={18}/><input value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} placeholder="Search staff by name or role..." /></div>
        <div className={styles.actions}><button className={styles.secondary} onClick={markAllPresent}><CheckCircle2 size={17}/> Mark all present</button><button className={styles.primary} onClick={submitAttendance}><Save size={17}/> Save attendance</button></div>
      </div>

      <div className={styles.layout}>
        <section className={styles.rosterCard}>
          <div className={styles.sectionHead}><div><span>DAILY ROSTER</span><h2>Team attendance</h2></div><div className={styles.rosterCount}>{filtered.length} staff</div></div>
          <div className={styles.desktopTable}>
            <table><thead><tr><th>Staff member</th><th>Role</th><th>Recorded time</th><th>Attendance status</th></tr></thead><tbody>
              {filtered.map(staff => <tr key={staff.id}><td><div className={styles.person}><span className={styles.avatar}>{staff.name?.[0] || 'S'}</span><strong>{staff.name}</strong></div></td><td className={styles.muted}>{staff.role || 'Team member'}</td><td className={styles.muted}>{staff.lastSeen}</td><td><StatusButtons staff={staff} onChange={handleStatusChange} styles={styles}/></td></tr>)}
            </tbody></table>
          </div>
          <div className={styles.mobileCards}>{filtered.map(staff => <article className={styles.staffCard} key={staff.id}><div className={styles.mobilePerson}><span className={styles.avatar}>{staff.name?.[0] || 'S'}</span><div><strong>{staff.name}</strong><p>{staff.role || 'Team member'} • {staff.lastSeen}</p></div></div><StatusButtons staff={staff} onChange={handleStatusChange} styles={styles}/></article>)}</div>
          {!filtered.length && <div className={styles.empty}>No staff match your search.</div>}
        </section>

        <aside className={styles.history}>
          <div className={styles.historyHead}><div><span>RECORDS</span><h2>Attendance history</h2></div><History size={20}/></div>
          <p className={styles.historyIntro}>Review previously submitted daily attendance logs.</p>
          <div className={styles.historyList}>{attendanceHistory.length ? attendanceHistory.map((record,i)=><div className={`${styles.historyItem} ${expandedLog===i?styles.expanded:''}`} key={i} onClick={()=>setExpandedLog(expandedLog===i?null:i)}><div className={styles.historyTop}><div><strong>{record.date}</strong><div className={styles.pills}><span className={styles.pillPresent}>{record.present} present</span><span className={styles.pillLate}>{record.late} late</span><span className={styles.pillAbsent}>{record.absent} absent</span></div></div><ChevronRight size={17}/></div>{expandedLog===i&&<div className={styles.details}>{record.details.map((p,idx)=><div key={idx}><span>{p.name}</span><b className={styles[`text_${p.status}`]}>{p.status}</b></div>)}</div>}</div>) : <div className={styles.noHistory}><Sparkles size={22}/><strong>No attendance history yet</strong><p>Saved attendance records will appear here.</p></div>}</div>
        </aside>
      </div>
    </div>
  );
}

function StatusButtons({ staff, onChange, styles }) {
  return <div className={styles.statusGroup}>
    <button className={staff.status==='present'?styles.selectedPresent:''} onClick={()=>onChange(staff.id,'present')}>Present</button>
    <button className={staff.status==='late'?styles.selectedLate:''} onClick={()=>onChange(staff.id,'late')}>Late</button>
    <button className={staff.status==='absent'?styles.selectedAbsent:''} onClick={()=>onChange(staff.id,'absent')}>Absent</button>
  </div>;
}
